import React from "react";
import { Box } from "@mui/material";
import {
  isBlackBelt,
  beltSupportsDegreeStripes,
  normalizeBeltKey,
  resolveBeltModel,
  stitchingColor,
  sleeveColor,
} from "../../belt/beltModel";

const BASE_HEIGHT = 20;

/**
 * Vector belt visual matching Android BeltView.kt:
 * body color, optional center stripe, stitching lines, rank sleeve with degree stripes.
 */
export default function BeltView({
  belt,
  stripes = 0,
  maxWidth = 150,
  maxHeight = 20,
  fullWidth = false,
  /** Compact tables: show more belt body color, less rank sleeve (Promotion by Group only). */
  colorFocus = false,
  sx = {},
  "aria-label": ariaLabel,
}) {
  const model = resolveBeltModel(belt);
  if (!model) return null;

  const scale = maxHeight / BASE_HEIGHT;
  const stripeCount = Math.max(0, Math.min(8, Number(stripes) || 0));
  const beltIsBlack = beltSupportsDegreeStripes(belt);

  let sleeveW;
  let tipGap;
  if (colorFocus) {
    const widthNum = typeof maxWidth === "number" ? maxWidth : 64;
    if (stripeCount > 0) {
      sleeveW = Math.min(Math.round(52 * scale), Math.round(widthNum * 0.36));
    } else if (beltIsBlack) {
      sleeveW = Math.round(widthNum * 0.22);
    } else {
      sleeveW = Math.max(5, Math.round(widthNum * 0.12));
    }
    tipGap = Math.max(3, Math.round(widthNum * 0.04));
  } else {
    sleeveW = Math.round(52 * scale);
    tipGap = Math.round(12 * scale);
  }

  const stitchOpacity = colorFocus && maxHeight <= 22 ? 0.2 : 1;
  const centerStripeH = Math.round(6 * scale);
  const borderRadius = Math.max(2, Math.round(4 * scale));
  const markerW = Math.max(1, Math.round(2 * scale));
  const markerGap = Math.max(2, Math.round(4 * scale));
  const stitchH = Math.max(1, scale >= 1 ? 1 : 1);
  const sleevePadX = Math.round(6 * scale);
  const sleevePadY = Math.max(1, Math.round(2 * scale));
  const bodyPadY = Math.max(1, Math.round(2 * scale));

  const outline =
    model.token === "white" ? "2px solid rgba(0,0,0,0.85)" : "1px solid rgba(0,0,0,0.2)";

  return (
    <Box
      role="img"
      aria-label={ariaLabel || belt}
      sx={{
        position: "relative",
        width: fullWidth ? "100%" : maxWidth,
        height: maxHeight,
        borderRadius: `${borderRadius}px`,
        overflow: "hidden",
        flexShrink: 0,
        boxSizing: "border-box",
        border: outline,
        ...(model.isCoral
          ? {
              backgroundColor: "#E31E24",
              backgroundImage: `repeating-linear-gradient(
                90deg,
                #E31E24 0,
                #E31E24 ${12 * scale}px,
                #1A1C23 ${12 * scale}px,
                #1A1C23 ${24 * scale}px
              )`,
            }
          : { backgroundColor: model.color }),
        ...sx,
      }}
    >
      {model.stripeColor ? (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            height: centerStripeH,
            backgroundColor: model.stripeColor,
          }}
        />
      ) : null}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-evenly",
          py: `${bodyPadY}px`,
          pointerEvents: "none",
        }}
      >
        {Array.from({ length: 3 }).map((_, i) => (
          <Box
            key={`stitch-${i}`}
            sx={{
              width: "100%",
              height: stitchH,
              backgroundColor: stitchingColor(belt),
              opacity: stitchOpacity,
            }}
          />
        ))}
      </Box>

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "stretch",
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            width: sleeveW,
            height: "100%",
            backgroundColor: sleeveColor(belt),
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: `${markerGap}px`,
            px: `${sleevePadX}px`,
            py: `${sleevePadY}px`,
            boxSizing: "border-box",
          }}
        >
          {Array.from({ length: stripeCount }).map((_, i) => (
            <Box
              key={`degree-${i}`}
              sx={{
                width: markerW,
                alignSelf: "stretch",
                borderRadius: `${Math.max(1, scale)}px`,
                backgroundColor: "#FFFFFF",
              }}
            />
          ))}
        </Box>
        <Box sx={{ width: tipGap, flexShrink: 0 }} />
      </Box>
    </Box>
  );
}

/**
 * Compact rank sleeve (degree stripes only) for split belt/stripe table columns.
 */
export function BeltRankSleeve({
  belt = "black",
  stripes = 0,
  maxWidth = 40,
  maxHeight = 14,
  sx = {},
}) {
  const stripeCount = Math.max(0, Math.min(8, Number(stripes) || 0));
  if (stripeCount === 0) return null;

  const scale = maxHeight / BASE_HEIGHT;
  const sleeveW = Math.max(Math.round(maxWidth * 0.82), Math.round(28 * scale));
  const borderRadius = Math.max(2, Math.round(4 * scale));
  const markerW = Math.max(1, Math.round(2 * scale));
  const markerGap = Math.max(2, Math.round(4 * scale));
  const sleevePadX = Math.round(4 * scale);
  const sleevePadY = Math.max(1, Math.round(2 * scale));

  return (
    <Box
      role="img"
      aria-label={`${stripeCount} stripes`}
      sx={{
        width: maxWidth,
        height: maxHeight,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "stretch",
        borderRadius: `${borderRadius}px`,
        overflow: "hidden",
        flexShrink: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: sleeveW,
          height: "100%",
          backgroundColor: sleeveColor(belt),
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: `${markerGap}px`,
          px: `${sleevePadX}px`,
          py: `${sleevePadY}px`,
          boxSizing: "border-box",
          borderRadius: `${borderRadius}px`,
        }}
      >
        {Array.from({ length: stripeCount }).map((_, i) => (
          <Box
            key={`sleeve-degree-${i}`}
            sx={{
              width: markerW,
              alignSelf: "stretch",
              borderRadius: `${Math.max(1, scale)}px`,
              backgroundColor: "#FFFFFF",
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export { isBlackBelt };
